import React from 'react';
import { motion } from 'framer-motion';
import { Check, User, Users, GraduationCap, ShieldCheck } from 'lucide-react';
import { Card } from '../ui/Card';
import { Account, AccountRole } from '../../types/accounts';
interface AccountSwitcherProps {
  accounts: Account[];
  activeAccountId: string;
  onSwitchAccount: (accountId: string) => void;
}
function getAccountIcon(role: AccountRole) {
  switch (role) {
    case 'learner':
      return User;
    case 'parent':
      return Users;
    case 'teacher':
      return GraduationCap;
    case 'admin':
      return ShieldCheck;
  }
}
function getAccountRoleLabel(role: AccountRole) {
  switch (role) {
    case 'learner':
      return 'Leerling';
    case 'parent':
      return 'Ouder';
    case 'teacher':
      return 'Docent';
    case 'admin':
      return 'Beheerder';
  }
}
function getAccountColor(role: AccountRole) {
  switch (role) {
    case 'learner':
      return {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        iconBg: 'bg-indigo-100',
        iconText: 'text-indigo-600',
        badgeBg: 'bg-indigo-100',
        badgeText: 'text-indigo-700',
        hoverBorder: 'hover:border-indigo-300'
      };
    case 'parent':
      return {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        iconBg: 'bg-emerald-100',
        iconText: 'text-emerald-600',
        badgeBg: 'bg-emerald-100',
        badgeText: 'text-emerald-700',
        hoverBorder: 'hover:border-emerald-300'
      };
    case 'teacher':
      return {
        bg: 'bg-violet-50',
        border: 'border-violet-200',
        iconBg: 'bg-violet-100',
        iconText: 'text-violet-600',
        badgeBg: 'bg-violet-100',
        badgeText: 'text-violet-700',
        hoverBorder: 'hover:border-violet-300'
      };
    case 'admin':
      return {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        iconBg: 'bg-amber-100',
        iconText: 'text-amber-600',
        badgeBg: 'bg-amber-100',
        badgeText: 'text-amber-700',
        hoverBorder: 'hover:border-amber-300'
      };
  }
}
export function AccountSwitcher({
  accounts,
  activeAccountId,
  onSwitchAccount
}: AccountSwitcherProps) {
  return <div className="space-y-3" data-id="element-5005">
      {accounts.map((account, index) => {
      const isActive = account.id === activeAccountId;
      const Icon = getAccountIcon(account.role);
      const colors = getAccountColor(account.role);
      return <motion.button key={account.id} onClick={() => onSwitchAccount(account.id)} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.05,
        duration: 0.3
      }} whileHover={{
        scale: isActive ? 1 : 1.02,
        y: -2
      }} whileTap={{
        scale: 0.98
      }} className={`
              w-full text-left p-4 rounded-xl border-2 transition-all duration-200
              ${isActive ? `${colors.bg} ${colors.border} shadow-md` : `bg-white border-slate-200 ${colors.hoverBorder} hover:shadow-sm`}
            `} data-id="element-5006">
            <div className="flex items-center gap-4" data-id="element-5007">
              <motion.div className={`
                  w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors
                  ${isActive ? `${colors.iconBg} ${colors.iconText}` : 'bg-slate-100 text-slate-500'}
                `} whileHover={{
            rotate: isActive ? 0 : 5
          }} transition={{
            duration: 0.2
          }} data-id="element-5008">
                <Icon className="w-6 h-6" data-id="element-5009" />
              </motion.div>

              <div className="flex-1 min-w-0" data-id="element-5010">
                <div className="flex items-center gap-2 mb-1" data-id="element-5011">
                  <h3 className="font-bold text-slate-900 truncate" data-id="element-5012">
                    {account.displayName}
                  </h3>
                  {isActive && <motion.div initial={{
                scale: 0,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 25
              }} className={`
                        flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold
                        ${colors.badgeBg} ${colors.badgeText}
                      `} data-id="element-5013">
                      <Check className="w-3 h-3" data-id="element-5014" />
                      Actief
                    </motion.div>}
                </div>
                <p className="text-sm text-slate-500" data-id="element-5015">
                  {getAccountRoleLabel(account.role)}
                </p>
              </div>
            </div>
          </motion.button>;
    })}
    </div>;
}